import { startHubLiveSession } from "@/lib/hubLiveBroker";
import {
  applyHubLivePoolEvent,
  getHubDbPoolHolderStreamState,
  resetHubDbPoolHolderStreamStateForTests as resetPoolState,
  subscribeHubDbPoolHolderStream,
  type HubDbPoolHolderStreamState,
  type HubDbPoolHolderStreamStatus,
} from "@/lib/hubDbPoolHolderStreamState";
import type { HubDbPoolHolderSnapshot } from "@/lib/slowRequestBanner";

export type { HubDbPoolHolderStreamState, HubDbPoolHolderStreamStatus };
export {
  applyHubLivePoolEvent,
  getHubDbPoolHolderStreamState,
  subscribeHubDbPoolHolderStream,
};

type HolderSseEvent =
  | { phase: "connected"; snapshot: HubDbPoolHolderSnapshot }
  | { phase: "holders"; snapshot: HubDbPoolHolderSnapshot }
  | { phase: "server_draining" };

function isHolderSnapshot(value: unknown): value is HubDbPoolHolderSnapshot {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (record.source !== "this_api_process" || !Array.isArray(record.holders)) {
    return false;
  }
  if (record.waiters != null && !Array.isArray(record.waiters)) {
    return false;
  }
  if (record.queues != null && !Array.isArray(record.queues)) {
    return false;
  }
  return true;
}

export function parseHubDbPoolHolderSseDataLine(line: string): HolderSseEvent | null {
  if (!line.startsWith("data: ")) {
    return null;
  }
  const payload = line.slice("data: ".length).trim();
  if (payload === "") {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(payload);
    if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    if (record.phase === "server_draining") {
      return { phase: "server_draining" };
    }
    if ((record.phase === "connected" || record.phase === "holders") && isHolderSnapshot(record.snapshot)) {
      return { phase: record.phase, snapshot: record.snapshot };
    }
    return null;
  } catch {
    return null;
  }
}

export function resetHubDbPoolHolderStreamStateForTests(): void {
  resetPoolState();
}

/**
 * Opens the browser-wide hub live socket (pool + lifecycle + cache).
 * Refcounted. Never opens a second EventSource.
 */
export function startHubDbPoolHolderStream(): () => void {
  return startHubLiveSession();
}
